import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Settings,
  Save,
  Trophy,
  Crown,
  Star,
  Award,
  User,
  DollarSign,
  Users,
  Target,
  RefreshCw,
} from 'lucide-react';

const RANK_ICONS = {
  neg: User,
  rm: Award,
  avp: Star,
  svp: Crown,
  evp: Trophy,
};

const RANK_COLORS = {
  neg: 'bg-blue-500',
  rm: 'bg-purple-500',
  avp: 'bg-pink-500',
  svp: 'bg-orange-500',
  evp: 'bg-red-500',
};

const AdminRankConfig = () => {
  const [requirements, setRequirements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadRequirements();
  }, []);

  const loadRequirements = async () => {
    try {
      const res = await api.get('/rank-requirements');
      setRequirements(res.data);
    } catch (error) {
      console.error('Error loading rank requirements:', error);
      toast.error('Failed to load rank requirements');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/rank-requirements', requirements);
      toast.success('Rank requirements saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save requirements');
    } finally {
      setSaving(false);
    }
  };

  const updateRequirement = (rank, field, value) => {
    setRequirements(prev => ({
      ...prev,
      [rank]: {
        ...prev[rank],
        requirements: {
          ...prev[rank].requirements,
          [field]: parseFloat(value) || 0
        }
      }
    }));
  };

  const updateMaintenance = (rank, field, value) => {
    setRequirements(prev => ({
      ...prev,
      [rank]: {
        ...prev[rank],
        maintenance: {
          ...prev[rank].maintenance,
          [field]: parseFloat(value) || 0
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  const ranks = ['neg', 'rm', 'avp', 'svp', 'evp'];

  return (
    <div className="p-6 space-y-6" data-testid="rank-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-purple-600" />
            Rank Requirements Configuration
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Configure the requirements for each agent rank promotion
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={loadRequirements}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button 
            className="bg-purple-600 hover:bg-purple-700"
            onClick={handleSave}
            disabled={saving}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Rank Hierarchy Visual */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="w-4 h-4 text-purple-600" />
            Rank Progression
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center gap-4">
            {ranks.map((rank, index) => {
              const Icon = RANK_ICONS[rank];
              const config = requirements?.[rank];
              return (
                <React.Fragment key={rank}>
                  <div className="flex flex-col items-center">
                    <div className={`w-16 h-16 rounded-full ${RANK_COLORS[rank]} flex items-center justify-center text-white`}>
                      <Icon className="w-8 h-8" />
                    </div>
                    <p className="font-semibold mt-2">{config?.rank_name || rank.toUpperCase()}</p>
                    <p className="text-xs text-gray-500">{config?.rank_display}</p>
                  </div>
                  {index < ranks.length - 1 && (
                    <div className="flex-shrink-0 text-gray-300">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Rank Requirements Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {ranks.filter(r => r !== 'neg').map((rank) => {
          const config = requirements?.[rank];
          const Icon = RANK_ICONS[rank];
          const prevRank = ranks[ranks.indexOf(rank) - 1];
          
          return (
            <Card key={rank} className="overflow-hidden">
              <div className={`${RANK_COLORS[rank]} p-4 text-white`}>
                <div className="flex items-center gap-3">
                  <Icon className="w-6 h-6" />
                  <div>
                    <h3 className="font-bold text-lg">{config?.rank_name || rank.toUpperCase()}</h3>
                    <p className="text-sm opacity-80">{config?.rank_display}</p>
                  </div>
                </div>
              </div>
              
              <CardContent className="p-4 space-y-4">
                {/* Promotion Requirements */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-green-600" />
                    Promotion Requirements (from {prevRank?.toUpperCase()})
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500 flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        Personal Sales (RM)
                      </label>
                      <Input
                        type="number"
                        value={config?.requirements?.personal_sales || 0}
                        onChange={(e) => updateRequirement(rank, 'personal_sales', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Team Commission (RM)
                      </label>
                      <Input
                        type="number"
                        value={config?.requirements?.team_commission || 0}
                        onChange={(e) => updateRequirement(rank, 'team_commission', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Required Downlines
                      </label>
                      <Input
                        type="number"
                        value={config?.requirements?.downline_count || 0}
                        onChange={(e) => updateRequirement(rank, 'downline_count', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Downline Rank Required</label>
                      <Select 
                        value={config?.requirements?.downline_rank || 'none'}
                        onValueChange={(v) => updateRequirement(rank, 'downline_rank', v === 'none' ? null : v)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Any Rank</SelectItem>
                          <SelectItem value="neg">NEG</SelectItem>
                          <SelectItem value="rm">RM</SelectItem>
                          <SelectItem value="avp">AVP</SelectItem>
                          <SelectItem value="svp">SVP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Maintenance Requirements (for AVP and SVP) */}
                {config?.maintenance && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-orange-600" />
                      Annual Maintenance Requirements
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm text-gray-500 flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          Personal Sales (RM)
                        </label>
                        <Input
                          type="number"
                          value={config?.maintenance?.personal_sales || 0}
                          onChange={(e) => updateMaintenance(rank, 'personal_sales', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-500 flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          Team Commission (RM)
                        </label>
                        <Input
                          type="number"
                          value={config?.maintenance?.team_commission || 0}
                          onChange={(e) => updateMaintenance(rank, 'team_commission', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <h4 className="font-semibold text-blue-800 mb-2">How Rank Progression Works</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>NEG (Negotiator)</strong>: Entry-level rank for all new agents</li>
            <li>• <strong>RM (Regional Manager)</strong>: Requires 1 active FT agent with 1 case + RM40K personal sales</li>
            <li>• <strong>AVP (Asst. Vice President)</strong>: Requires 1 active RM + RM60K personal + RM40K team commission</li>
            <li>• <strong>SVP (Sr. Vice President)</strong>: Requires 2 active AVPs + RM50K personal + RM200K team commission</li>
            <li>• <strong>EVP (Exec. Vice President)</strong>: Requires 2 active SVPs + RM30K personal + RM400K team commission</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminRankConfig;
